import React from 'react';
import { Drawer, Steps, Tag, Typography, Divider, Button, Space, Result, Spin, Card, message } from 'antd';
import { 
  CheckCircleOutlined, 
  SyncOutlined, 
  CloseCircleOutlined, 
  ClockCircleOutlined,
  PlayCircleOutlined
} from '@ant-design/icons';
import { AdminTask, AdminTaskStatus, FullAutoChapterTaskDetail, AdminSubtask } from '../../../types';
import { formatDate } from '../../../utils';
import { adminTasksApi } from '../../../services/api/adminTasks';

const { Title, Text, Paragraph } = Typography;

interface TaskDetailDrawerProps {
  visible: boolean;
  onClose: () => void;
  taskId: string | null;
}

const TaskDetailDrawer: React.FC<TaskDetailDrawerProps> = ({ visible, onClose, taskId }) => {
  const [task, setTask] = React.useState<AdminTask<FullAutoChapterTaskDetail> | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const fetchTaskDetail = React.useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const response = await adminTasksApi.getTaskDetail(taskId);
      setTask(response.data as any);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Không thể tải chi tiết task');
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  React.useEffect(() => {
    if (visible && taskId) {
      fetchTaskDetail();
      
      // Tự động reload mỗi 5 giây nếu task đang chạy
      const timer = setInterval(() => {
        if (task && (task.status === AdminTaskStatus.IN_PROGRESS || task.status === AdminTaskStatus.PENDING)) {
          fetchTaskDetail();
        }
      }, 5000);
      
      return () => clearInterval(timer);
    }
  }, [visible, taskId, task?.status, fetchTaskDetail]);

  const getStatusTag = (status: AdminTaskStatus) => {
    switch (status) {
      case AdminTaskStatus.COMPLETED:
        return <Tag color="success" icon={<CheckCircleOutlined />}>Hoàn thành</Tag>;
      case AdminTaskStatus.IN_PROGRESS:
        return <Tag color="processing" icon={<SyncOutlined spin />}>Đang chạy</Tag>;
      case AdminTaskStatus.FAILED:
        return <Tag color="error" icon={<CloseCircleOutlined />}>Thất bại</Tag>;
      case AdminTaskStatus.PENDING:
        return <Tag color="default" icon={<ClockCircleOutlined />}>Chờ xử lý</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const getStepStatus = (status: AdminTaskStatus) => {
    switch (status) {
      case AdminTaskStatus.COMPLETED: return 'finish';
      case AdminTaskStatus.IN_PROGRESS: return 'process';
      case AdminTaskStatus.FAILED: return 'error';
      case AdminTaskStatus.PENDING: return 'wait';
      default: return 'wait';
    }
  };

  const handleResume = async () => {
    if (!taskId) return;
    try {
      await adminTasksApi.resumeTask(taskId);
      message.success('Đã gửi yêu cầu Resume tiến trình thành công!');
      fetchTaskDetail(); // Refresh data
    } catch (err: any) {
      message.error('Không thể Resume tiến trình: ' + (err.message || 'Lỗi không xác định'));
    }
  };

  if (!taskId) return null;

  return (
    <Drawer
      title="Chi tiết tiến trình Full Auto"
      placement="right"
      width={600}
      onClose={onClose}
      open={visible}
      extra={
        <Space>
          <Button onClick={fetchTaskDetail} icon={<SyncOutlined />}>Làm mới</Button>
          {task?.status === AdminTaskStatus.FAILED && (
            <Button type="primary" icon={<PlayCircleOutlined />} onClick={handleResume}>Resume</Button>
          )}
        </Space>
      }
    >
      {loading && !task ? (
        <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>
      ) : error ? (
        <Result status="error" title="Lỗi" subTitle={error} />
      ) : task ? (
        <div>
          <Title level={4}>{task.detail.input.storyName}</Title>
          <Paragraph>
            <Text type="secondary">Chapter: </Text> <b>{task.detail.input.chapterName}</b>
          </Paragraph>
          <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
            <div><Text type="secondary">Trạng thái: </Text> {getStatusTag(task.status)}</div>
            <div><Text type="secondary">Khởi tạo: </Text> {formatDate(task.createdAt)}</div>
          </div>

          {task.detail.error && (
            <Card size="small" style={{ backgroundColor: '#fff2f0', borderColor: '#ffccc7', marginBottom: '20px' }}>
              <Text type="danger"><b>Lỗi hệ thống:</b> {task.detail.error}</Text>
            </Card>
          )}

          <Divider orientation={"left" as any}>Pipeline Execution</Divider>
          
          <Steps
            direction="vertical"
            size="small"
            current={task.detail.subtasks?.findIndex((s: AdminSubtask) => s.status !== AdminTaskStatus.COMPLETED)}
            items={(task.detail.subtasks || []).map((st: AdminSubtask) => ({
              title: st.name,
              status: getStepStatus(st.status),
              description: (
                <div style={{ marginTop: '4px' }}>
                  {st.message && <Text type={st.status === AdminTaskStatus.FAILED ? 'danger' : 'secondary'} style={{ fontSize: '12px' }}>{st.message}</Text>}
                  {st.result && (
                    <div style={{ fontSize: '11px', marginTop: '4px', backgroundColor: '#f5f5f5', padding: '4px', borderRadius: '4px' }}>
                      <code>{JSON.stringify(st.result)}</code>
                    </div>
                  )}
                </div>
              ),
            }))}
          />

          <Divider orientation={"left" as any}>Cấu hình đầu vào</Divider>
          <div style={{ fontSize: '13px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div><Text type="secondary">Ngưỡng Outline:</Text> {task.detail.input.outlinePassScore}%</div>
              <div><Text type="secondary">Retry Outline:</Text> {task.detail.input.maxOutlineRetries} lần</div>
              <div><Text type="secondary">Ngưỡng Content:</Text> {task.detail.input.contentPassScore}%</div>
              <div><Text type="secondary">Retry Content:</Text> {task.detail.input.maxContentRetries} lần</div>
              <div><Text type="secondary">Media Type:</Text> <Tag>{task.detail.input.panelMediaType}</Tag></div>
            </div>
            {task.detail.input.instruction && (
              <div style={{ marginTop: '8px' }}>
                <Text type="secondary">Chỉ dẫn:</Text>
                <div style={{ backgroundColor: '#fafafa', padding: '8px', border: '1px solid #f0f0f0', borderRadius: '4px', marginTop: '4px' }}>
                  {task.detail.input.instruction}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </Drawer>
  );
};

export default TaskDetailDrawer;
